import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store';
import { useQuery } from '@tanstack/react-query';
import {
  Plus,
  MagnifyingGlass as Search,
  DownloadSimple as Download,
  Wallet,
  Receipt,
  TrendUp as TrendingUp,
  WarningCircle,
  Timer,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/shared/PageHeader';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RowActions } from '@/components/shared/RowActions';
import { StatCard } from '@/components/shared/StatCard';
import { useTimeEntriesStore } from '@/lib/timeEntriesStore';
import { NewFeeNoteModal } from '@/components/shared/NewFeeNoteModal';
import { RecordPaymentModal } from '@/components/payments/RecordPaymentModal';
import { fetchFeeNotes, fetchAgedDebt, fetchFeeNoteStats, STATUS_LABELS as FEE_NOTE_STATUS_LABEL, type FeeNoteStatus } from '@/lib/feeNotesApi';
import { fetchActivityTotals } from '@/lib/activitiesApi';
import { api } from '@/lib/api';
import { FeeNoteActionsButton } from '@/components/fee-notes/FeeNoteActionsButton';
import { exportToCsv } from '@/lib/exportUtils';

function feeNoteStatusVariant(s: FeeNoteStatus): 'default' | 'success' | 'warning' | 'danger' | 'info' | 'gold' | 'outline' {
  switch (s) {
    case 'draft': return 'default';
    case 'pending_approval': return 'warning';
    case 'approved': return 'info';
    case 'sending': return 'info';        // dispatch in progress
    case 'sent': return 'info';
    case 'partially_paid': return 'gold';
    case 'paid': return 'success';
    case 'overdue': return 'danger';
    case 'cancelled': return 'outline';
    case 'written_off': return 'outline';
    case 'send_failed': return 'danger';  // alert: email dispatch failed
    case 'partially_credited': return 'gold';
    case 'credited': return 'success';
  }
}

type Tab = 'records' | 'notes' | 'outstanding' | 'jobs';
type NotesSub = 'draft' | 'pending' | 'unpaid' | 'paid' | 'all';


export default function FeesPage() {
  const [tab, setTab] = useState<Tab>('records');
  const [notesSub, setNotesSub] = useState<NotesSub>('all');
  const [recordSearch, setRecordSearch] = useState('');
  const [feeNoteOpen, setFeeNoteOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  // Real fee-notes from /api/v1/fee-notes — replaces the previous NOTES mock array.
  // Mock NOTES is still defined above so any unrelated component using it
  // doesn't break, but this page now renders live data.
  const feeNotesQ = useQuery({
    queryKey: ['fee-notes'],
    queryFn: () => fetchFeeNotes({ limit: 100 }),
  });
  const agedDebtQ = useQuery({
    queryKey: ['fee-notes', 'aged-debt'],
    queryFn: fetchAgedDebt,
  });

  // Server-calculated stats — queries ALL fee notes, never limited by pagination
  const feeStatsQ = useQuery({
    queryKey: ['fee-notes', 'stats'],
    queryFn: fetchFeeNoteStats,
    staleTime: 30_000,
  });
  const feeStats = feeStatsQ.data ?? {
    outstanding: 0,
    outstandingCount: 0,
    overdue: 0,
    overdueCount: 0,
    totalBilled: 0,
    totalCollected: 0,
    collectionRate: 0,
  };

  // MTD activity totals for the "time recorded this month" widget only
  const now = new Date();
  const mtdStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const mtdTotalsQ = useQuery({
    queryKey: ['activity-totals', mtdStart],
    queryFn: () => fetchActivityTotals({ dateFrom: mtdStart }),
  });

  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const basePath = isClerk ? '/clerk' : '/admin';

  const timeEntries = useTimeEntriesStore((s) => s.entries);
  const deleteTimeEntry = useTimeEntriesStore((s) => s.deleteEntry);
  const fetchEntries = useTimeEntriesStore((s) => s.fetchEntries);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const allRecords = [
    ...timeEntries.map((te) => ({
      id: te.id,
      date: te.date,
      case: te.matterLabel,
      matterId: te.matterId,
      barrister: te.barrister,
      kind: te.kind,
      hours: te.hours,
      amount: te.amount,
      status: te.status,
      vat: te.vat,
      description: te.description,
      fromTimer: true,
    })),
  ];

  const searchLower = recordSearch.toLowerCase();
  const filteredRecords = recordSearch
    ? allRecords.filter(
        (r) =>
          r.case.toLowerCase().includes(searchLower) ||
          r.barrister.toLowerCase().includes(searchLower) ||
          r.kind.toLowerCase().includes(searchLower),
      )
    : allRecords;

  const allFeeNotes = feeNotesQ.data?.feeNotes ?? [];
  // The sub-tab keys ("all", "draft", "pending", "unpaid", "paid") need to map
  // to the real status enum. Pending = pending_approval; unpaid = sent | overdue | partially_paid.
  const filteredFeeNotes = (() => {
    if (notesSub === 'all') return allFeeNotes;
    if (notesSub === 'draft') return allFeeNotes.filter((n) => n.status === 'draft');
    if (notesSub === 'pending') return allFeeNotes.filter((n) => n.status === 'pending_approval' || n.status === 'approved');
    if (notesSub === 'unpaid') return allFeeNotes.filter((n) => n.status === 'sent' || n.status === 'overdue' || n.status === 'partially_paid');
    if (notesSub === 'paid') return allFeeNotes.filter((n) => n.status === 'paid');
    return allFeeNotes;
  })();

  const handleExport = () => {
    if (tab === 'notes') {
      const notes = filteredFeeNotes;
      if (notes.length === 0) {
        toast.info('No fee notes to export');
        return;
      }
      exportToCsv(
        notes.map((n) => ({
          number:       n.feeNoteNumber,
          matter_ref:   n.case?.caseNumber ?? '',
          matter_name:  n.case?.caseName ?? '',
          issue_date:   new Date(n.issueDate).toLocaleDateString('en-GB'),
          due_date:     new Date(n.dueDate).toLocaleDateString('en-GB'),
          net_gbp:      Number(n.subtotal).toFixed(2),
          vat_gbp:      Number(n.vatTotal).toFixed(2),
          gross_gbp:    Number(n.total).toFixed(2),
          paid_gbp:     Number(n.amountPaid).toFixed(2),
          balance_gbp:  n.outstanding.toFixed(2), // server-authoritative, refund-aware
          status:       FEE_NOTE_STATUS_LABEL[n.status] ?? n.status,
        })),
        `fee-notes-accountant-${new Date().toISOString().slice(0, 10)}`,
        [
          { key: 'number',      label: 'Fee Note #'    },
          { key: 'matter_ref',  label: 'Matter Ref'    },
          { key: 'matter_name', label: 'Matter'        },
          { key: 'issue_date',  label: 'Issue Date'    },
          { key: 'due_date',    label: 'Due Date'      },
          { key: 'net_gbp',     label: 'Net (£)'       },
          { key: 'vat_gbp',     label: 'VAT (£)'       },
          { key: 'gross_gbp',   label: 'Gross (£)'     },
          { key: 'paid_gbp',    label: 'Paid (£)'      },
          { key: 'balance_gbp', label: 'Balance (£)'   },
          { key: 'status',      label: 'Status'        },
        ],
      );
      toast.success(`Exported ${notes.length} fee note${notes.length !== 1 ? 's' : ''} — accountant format`);
    } else if (tab === 'outstanding') {
      const debt = agedDebtQ.data ?? [];
      if (debt.length === 0) {
        toast.info('No outstanding debt to export');
        return;
      }
      exportToCsv(
        debt.map((d) => ({
          client:       d.clientName,
          matters:      d.mattersCount,
          current_gbp:  d.current.toFixed(2),
          days_30_gbp:  d.d30.toFixed(2),
          days_60_gbp:  d.d60.toFixed(2),
          days_90_gbp:  d.d90.toFixed(2),
          total_gbp:    d.total.toFixed(2),
        })),
        `aged-debt-${new Date().toISOString().slice(0, 10)}`,
        [
          { key: 'client',      label: 'Client'            },
          { key: 'matters',     label: 'Matters'           },
          { key: 'current_gbp', label: 'Not Yet Due (£)'   },
          { key: 'days_30_gbp', label: '1–30 Days (£)'     },
          { key: 'days_60_gbp', label: '31–60 Days (£)'    },
          { key: 'days_90_gbp', label: '61+ Days (£)'      },
          { key: 'total_gbp',   label: 'Total Outstanding (£)' },
        ],
      );
      toast.success(`Exported aged debt for ${debt.length} client${debt.length !== 1 ? 's' : ''}`);
    } else {
      // Records tab — export time/fee records
      const records = filteredRecords;
      if (records.length === 0) {
        toast.info('No fee records to export');
        return;
      }
      exportToCsv(
        records.map((r) => ({
          date:        r.date,
          matter:      r.case,
          barrister:   r.barrister,
          description: r.description ?? '',
          hours:       r.hours ?? '',
          amount_gbp:  r.amount != null ? Number(r.amount).toFixed(2) : '',
          status:      r.status,
        })),
        `fee-records-${new Date().toISOString().slice(0, 10)}`,
        [
          { key: 'date',        label: 'Date'        },
          { key: 'matter',      label: 'Matter'      },
          { key: 'barrister',   label: 'Barrister'   },
          { key: 'description', label: 'Description' },
          { key: 'hours',       label: 'Hours'       },
          { key: 'amount_gbp',  label: 'Amount (£)'  },
          { key: 'status',      label: 'Status'      },
        ],
      );
      toast.success(`Exported ${records.length} fee record${records.length !== 1 ? 's' : ''}`);
    }
  };

  // View = open the fee-note PDF (generates server-side on demand if absent).
  // Advanced flows (regenerate, send, chase, credit) stay in FeeNoteActionsButton.
  const viewFeeNotePdf = async (id: string, feeNoteNumber: string) => {
    try {
      const res = await api.get(`/fee-notes/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch {
      toast.error(`Could not open ${feeNoteNumber}`);
    }
  };

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Fees & Billing"
        subtitle="Brief fees, refreshers, hourly, opinions — fee notes and disbursements"
        actions={
          <>
            <Button variant="outline" size="md" leftIcon={<Download className="h-4 w-4" />} onClick={handleExport}>Export</Button>
            <Button variant="outline" size="md" onClick={() => setPaymentOpen(true)}>Record payment</Button>
            <Button variant="primary" size="md" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setFeeNoteOpen(true)}>New fee note</Button>
          </>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard
          icon={<Wallet className="h-4 w-4" />}
          label="Billed (MTD)"
          value={mtdTotalsQ.isLoading ? '...' : mtdTotalsQ.isError ? '—' : `£${((mtdTotalsQ.data?.billableAmount ?? 0) / 1000).toFixed(0)}k`}
        />
        {/* These three read feeStatsQ — a failed load shows "—", never a reassuring £0.0k. */}
        <StatCard
          icon={<Receipt className="h-4 w-4" />}
          label="Outstanding"
          value={feeStatsQ.isLoading ? '...' : feeStatsQ.isError ? '—' : `£${(feeStats.outstanding / 1000).toFixed(1)}k`}
          hint={feeStatsQ.isError ? "Couldn't load" : `${feeStats.outstandingCount} fee note${feeStats.outstandingCount !== 1 ? 's' : ''}`}
        />
        <StatCard
          icon={<WarningCircle className="h-4 w-4" />}
          label="Overdue"
          value={feeStatsQ.isLoading ? '...' : feeStatsQ.isError ? '—' : `£${(feeStats.overdue / 1000).toFixed(1)}k`}
        />
        <StatCard
          icon={<TrendingUp className="h-4 w-4" />}
          label="Collection rate"
          value={feeStatsQ.isLoading ? '...' : feeStatsQ.isError ? '—' : `${feeStats.collectionRate}%`}
        />
      </div>

      {/* Tabs */}
      {/* Scrollable at phone width — "Outstanding balances" was sliced in half and "Job status"
          sat past the right edge with no way to reach it. The overlap pixel sits on the SCROLLER
          rather than on each button, so nothing protrudes vertically for `overflow-y` to scroll.
          Same shape as the barrister's My Fees tabs. */}
      <div className="no-scrollbar -mb-px mb-4 flex items-center gap-1 overflow-x-auto overflow-y-hidden border-b border-border">
        {([
          { key: 'records',     label: 'Activities'         },
          { key: 'notes',       label: 'Fee notes'          },
          { key: 'outstanding', label: 'Outstanding balances'},
          { key: 'jobs',        label: 'Job status'         },
        ] as const).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`shrink-0 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition ${
              tab === t.key
                ? 'border-brand-600 text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'records' && (
        <Card>
          {/* The search box asked for a fixed 288px and the row could not wrap, so on a phone the
              card was wider than the screen and "New fee record" sat outside it. The box is now
              fluid below `sm` and the row wraps; from `sm` up both are what they were. */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                type="search"
                placeholder="Filter by matter, barrister, kind…"
                value={recordSearch}
                onChange={(e) => setRecordSearch(e.target.value)}
                className="h-9 w-full min-w-0 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none sm:w-72"
              />
            </div>
            <Button variant="primary" size="sm" className="shrink-0 whitespace-nowrap" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setFeeNoteOpen(true)}>New fee record</Button>
          </div>
          <div className="overflow-x-auto">
            {/* A table cannot reflow: on a phone its right-hand columns ran off the screen with no way to reach them. */}
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Matter</th>
                  <th>Barrister</th>
                  <th>Kind</th>
                  <th>Description</th>
                  <th>Hours</th>
                  <th>Net</th>
                  <th>VAT</th>
                  <th>Status</th>
                  <th className="w-10 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map((r) => {
                  // Format date as "12 May" (short)
                  const dateObj = new Date(r.date);
                  const formattedDate = dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                  // Split matter into number and name
                  const matterParts = r.case.match(/^(MAT-\d{4}-\d{3})\s+(.+)$/);
                  const matterNum = matterParts?.[1] ?? r.case.split(' ')[0];
                  const matterName = matterParts?.[2] ?? r.case.split(' ').slice(1).join(' ');

                  return (
                  <tr key={r.id} className="cursor-pointer">
                    <td className="w-20 text-sm text-muted-foreground">{formattedDate}</td>
                    <td>
                      <button
                        className="text-left hover:underline"
                        onClick={(e) => { e.stopPropagation(); if (r.matterId) navigate(`${basePath}/matters/${r.matterId}`); }}
                      >
                        <span className="text-sm font-semibold text-brand-600 dark:text-brand-400">{matterNum}</span>
                        <span className="ml-2 text-sm text-foreground">{matterName}</span>
                      </button>
                    </td>
                    <td className="text-sm">{r.barrister}</td>
                    <td className="flex items-center gap-1.5">
                      <Badge>{r.kind}</Badge>
                      {r.fromTimer && (
                        <Badge variant="gold">
                          <Timer className="mr-0.5 inline h-3 w-3" weight="bold" />
                          Timer
                        </Badge>
                      )}
                    </td>
                    <td className="text-sm">{r.description}</td>
                    <td className="text-sm text-muted-foreground">{r.hours}</td>
                    <td className="text-sm font-semibold">£{r.amount.toLocaleString()}</td>
                    <td className="text-sm text-muted-foreground">£{r.vat.toLocaleString()}</td>
                    <td>
                      <Badge variant={r.status === 'Billable' ? 'info' : 'default'}>
                        {r.status}
                      </Badge>
                    </td>
                    <td>
                      {r.fromTimer && (
                        <RowActions
                          onDelete={async () => {
                            // Same reasoning as MyFeesPage — never announce a deletion the
                            // server may have refused.
                            if (await deleteTimeEntry(r.id)) toast.success('Time entry deleted');
                          }}
                          deleteConfirm={{
                            title: 'Delete time entry?',
                            message: 'Delete this time entry? This cannot be undone.',
                            confirmLabel: 'Delete',
                          }}
                        />
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === 'notes' && (
        <>
          {/* max-w-full is what makes the scroll bite — without it the group sizes to its five
              pills and simply hangs off the edge, taking "Paid" with it. */}
          <div className="no-scrollbar mb-4 flex max-w-full items-center gap-1 overflow-x-auto overflow-y-hidden rounded-md border border-border bg-card p-0.5 text-xs">
            {([
              { key: 'all',     label: 'All',     count: allFeeNotes.length },
              { key: 'draft',   label: 'Draft',   count: allFeeNotes.filter((n) => n.status === 'draft').length },
              { key: 'pending', label: 'Pending approval', count: allFeeNotes.filter((n) => n.status === 'pending_approval' || n.status === 'approved').length },
              { key: 'unpaid',  label: 'Unpaid',  count: allFeeNotes.filter((n) => n.status === 'sent' || n.status === 'overdue' || n.status === 'partially_paid').length },
              { key: 'paid',    label: 'Paid',    count: allFeeNotes.filter((n) => n.status === 'paid').length },
            ] as const).map((s) => (
              <button
                key={s.key}
                onClick={() => setNotesSub(s.key as NotesSub)}
                className={`shrink-0 whitespace-nowrap rounded-sm px-3 py-1.5 font-medium ${
                  notesSub === s.key ? 'bg-brand-600 text-white' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {s.label} <span className="ml-1 opacity-70">{s.count}</span>
              </button>
            ))}
          </div>

          <Card>
            <div className="overflow-x-auto">
              {/* A table cannot reflow: on a phone its right-hand columns ran off the screen with no way to reach them. */}
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Number</th>
                    <th>Matter</th>
                    <th>Client</th>
                    <th>Net</th>
                    <th>VAT</th>
                    <th>Total</th>
                    <th>Issued</th>
                    <th>Due</th>
                    <th>Status</th>
                    <th className="w-10 whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {feeNotesQ.isLoading && (
                    <tr>
                      <td colSpan={10} className="py-6 text-center text-sm text-muted-foreground">
                        Loading fee notes…
                      </td>
                    </tr>
                  )}
                  {feeNotesQ.isError && (
                    <tr><td colSpan={10}><QueryErrorState compact what="fee notes" onRetry={() => feeNotesQ.refetch()} /></td></tr>
                  )}
                  {feeNotesQ.data && filteredFeeNotes.length === 0 && (
                    <tr>
                      <td colSpan={10} className="py-8 text-center text-sm text-muted-foreground">
                        No fee notes in this view.
                      </td>
                    </tr>
                  )}
                  {filteredFeeNotes.map((n) => (
                    <tr key={n.id} className="cursor-pointer">
                      <td className="text-sm font-medium text-brand-700 dark:text-brand-300">{n.feeNoteNumber}</td>
                      <td className="font-medium text-foreground">
                        {n.case ? `${n.case.caseNumber} ${n.case.caseName}` : '—'}
                      </td>
                      <td className="text-muted-foreground">—</td>
                      <td className="text-sm text-muted-foreground">£{Number(n.subtotal).toLocaleString('en-GB', { maximumFractionDigits: 2 })}</td>
                      <td className="text-sm text-muted-foreground">£{Number(n.vatTotal).toLocaleString('en-GB', { maximumFractionDigits: 2 })}</td>
                      <td className="text-sm font-semibold text-foreground">£{Number(n.total).toLocaleString('en-GB', { maximumFractionDigits: 2 })}</td>
                      <td className="whitespace-nowrap text-sm text-muted-foreground">{new Date(n.issueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</td>
                      <td className="whitespace-nowrap text-sm text-muted-foreground">{new Date(n.dueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</td>
                      <td>
                        <Badge variant={feeNoteStatusVariant(n.status)}>
                          {FEE_NOTE_STATUS_LABEL[n.status] ?? n.status}
                        </Badge>
                      </td>
                      <td>
                        <div className="inline-flex items-center gap-0.5">
                          <RowActions
                            onView={() => viewFeeNotePdf(n.id, n.feeNoteNumber)}
                            viewLabel="View PDF"
                          />
                          <FeeNoteActionsButton feeNote={n} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {tab === 'outstanding' && (
        <Card>
          <div className="border-b border-border px-6 py-3 text-sm text-muted-foreground">
            Aged debt by client · days past due date
          </div>
          <div className="overflow-x-auto">
            {/* A table cannot reflow: on a phone its right-hand columns ran off the screen with no way to reach them. */}
            <table className="data-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Matters</th>
                  <th>Current</th>
                  <th>30 days</th>
                  <th>60 days</th>
                  <th>90+ days</th>
                  <th>Total outstanding</th>
                </tr>
              </thead>
              <tbody>
                {agedDebtQ.isLoading && (
                  <tr><td colSpan={7} className="py-6 text-center text-sm text-muted-foreground">Loading aged debt…</td></tr>
                )}
                {agedDebtQ.isError && (
                  <tr><td colSpan={7}><QueryErrorState compact what="aged debt" onRetry={() => agedDebtQ.refetch()} /></td></tr>
                )}
                {agedDebtQ.data && agedDebtQ.data.length === 0 && (
                  <tr><td colSpan={7} className="py-8 text-center text-sm text-muted-foreground">No outstanding balances — all fee notes are paid or draft.</td></tr>
                )}
                {(agedDebtQ.data ?? []).map((o) => (
                  <tr key={o.contactId ?? o.clientName} className="cursor-pointer">
                    <td className="font-medium text-foreground">{o.clientName}</td>
                    <td className="text-sm text-muted-foreground">{o.mattersCount}</td>
                    <td className="text-sm text-muted-foreground">{o.current > 0 ? `£${o.current.toLocaleString('en-GB', { maximumFractionDigits: 2 })}` : '—'}</td>
                    <td className="text-sm text-muted-foreground">{o.d30 > 0 ? `£${o.d30.toLocaleString('en-GB', { maximumFractionDigits: 2 })}` : '—'}</td>
                    <td className={`text-sm ${o.d60 > 0 ? 'font-medium text-amber-700 dark:text-amber-300' : 'text-muted-foreground'}`}>{o.d60 > 0 ? `£${o.d60.toLocaleString('en-GB', { maximumFractionDigits: 2 })}` : '—'}</td>
                    <td className={`text-sm ${o.d90 > 0 ? 'font-medium text-red-700 dark:text-red-300' : 'text-muted-foreground'}`}>{o.d90 > 0 ? `£${o.d90.toLocaleString('en-GB', { maximumFractionDigits: 2 })}` : '—'}</td>
                    <td className="text-sm font-semibold text-foreground">£{o.total.toLocaleString('en-GB', { maximumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === 'jobs' && (
        <Card>
          <div className="px-6 py-12 text-center">
            <div className="text-sm font-medium text-foreground">No background jobs to show</div>
            <p className="mx-auto mt-2 max-w-md text-xs leading-relaxed text-muted-foreground">
              Bulk fee-note generation, reminder runs and VAT exports will surface here once the
              corresponding backend jobs are wired.
            </p>
          </div>
        </Card>
      )}

      <NewFeeNoteModal
        open={feeNoteOpen}
        onClose={() => setFeeNoteOpen(false)}
        onSave={() => setFeeNoteOpen(false)}
      />

      {paymentOpen && (
        <RecordPaymentModal
          onClose={() => setPaymentOpen(false)}
          onSaved={() => setPaymentOpen(false)}
        />
      )}
    </div>
  );
}
